// src/pages/Journaux.js - Journal d'activité (module "Journaux").
// Chaque société consulte son propre historique (utilisateurs web ET mobile
// confondus, distingués par un badge) ; l'admin voit tout, toutes sociétés.
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import Layout from "../components/Layout";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://localhost/securecontrol-backend";

const COLORS = {
  primary: '#0B3C5D',
  secondary: '#1E88C8',
  darkText: '#2E2E2E',
  lightBg: '#F5F7FA',
  white: '#FFFFFF',
  danger: '#f44336',
  success: '#4caf50',
  warning: '#ff9800',
};

const PROFILE_LABELS = {
  1: 'Administrateur',
  2: 'Contrôleur',
  3: 'Gestionnaire',
  4: 'Agent',
  5: 'Poste de contrôle',
};

const aujourdHui = () => new Date().toISOString().slice(0, 10);

const Journaux = () => {
  const [userInfo, setUserInfo] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [dateDebut, setDateDebut] = useState(aujourdHui());
  const [dateFin, setDateFin] = useState(aujourdHui());
  const [filtrePlateforme, setFiltrePlateforme] = useState("tous");
  const [filtreAction, setFiltreAction] = useState("");

  useEffect(() => {
    const stored = localStorage.getItem("user");
    if (stored) {
      try {
        const u = JSON.parse(stored);
        setUserInfo({ ...u, profile: u.profile || u.poste || 0 });
      } catch {
        // ignore
      }
    }
  }, []);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("access_token");
      const params = {};
      if (dateDebut) params.date_debut = dateDebut;
      if (dateFin) params.date_fin = dateFin;
      if (filtrePlateforme !== "tous") params.plateforme = filtrePlateforme;
      if (filtreAction.trim()) params.action = filtreAction.trim();
      const response = await axios.get(`${API_BASE_URL}/journal/list.php`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setLogs(response.data?.logs || []);
    } catch (err) {
      setError(err.response?.data?.message || "Impossible de charger le journal d'activité.");
    } finally {
      setLoading(false);
    }
  }, [dateDebut, dateFin, filtrePlateforme, filtreAction]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const reinitialiserPeriode = () => {
    setDateDebut(aujourdHui());
    setDateFin(aujourdHui());
  };

  const formatDate = (d) => d ? new Date(d.replace(' ', 'T')).toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
  }) : '-';

  const isAdmin = userInfo && Number(userInfo.profile) === 1;

  const compteurs = logs.reduce((acc, l) => {
    acc[l.plateforme] = (acc[l.plateforme] || 0) + 1;
    return acc;
  }, {});

  if (userInfo && ![1, 3, 5].includes(Number(userInfo.profile))) {
    return (
      <Layout>
        <div className="pagetitle"><h1>Journaux</h1></div>
        <div className="alert alert-warning m-4">Réservé aux administrateurs, gestionnaires et postes de contrôle.</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="pagetitle">
        <h1>Journaux</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">Tableau de bord</li>
            <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Journaux</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="alert alert-info d-flex align-items-start mb-4" style={{ borderRadius: '10px' }}>
          <i className="bi bi-info-circle me-2 mt-1"></i>
          <div>
            Historique des actions effectuées par les utilisateurs de {isAdmin ? "toutes les sociétés" : "votre société"},
            depuis l'application web comme depuis l'application mobile.
          </div>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}

        <div className="card mb-4" style={{ borderRadius: '10px' }}>
          <div className="card-body d-flex flex-wrap gap-3 align-items-end justify-content-between">
            <div className="d-flex flex-wrap gap-3 align-items-end">
              <div>
                <label className="form-label small fw-semibold mb-1" style={{ color: COLORS.darkText }}>Date début</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={dateDebut}
                  max={dateFin || undefined}
                  onChange={(e) => setDateDebut(e.target.value)}
                  style={{ borderRadius: '8px' }}
                />
              </div>
              <div>
                <label className="form-label small fw-semibold mb-1" style={{ color: COLORS.darkText }}>Date fin</label>
                <input
                  type="date"
                  className="form-control form-control-sm"
                  value={dateFin}
                  min={dateDebut || undefined}
                  onChange={(e) => setDateFin(e.target.value)}
                  style={{ borderRadius: '8px' }}
                />
              </div>
              <div>
                <label className="form-label small fw-semibold mb-1" style={{ color: COLORS.darkText }}>Action</label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  placeholder="ex: login, incident_create..."
                  value={filtreAction}
                  onChange={(e) => setFiltreAction(e.target.value)}
                  style={{ borderRadius: '8px', minWidth: '200px' }}
                />
              </div>
              <button
                className="btn btn-sm"
                onClick={reinitialiserPeriode}
                style={{ color: COLORS.primary, border: `1px solid ${COLORS.primary}`, borderRadius: '8px' }}
              >
                Aujourd'hui
              </button>
            </div>
            <button className="btn btn-sm" onClick={fetchLogs} style={{ color: COLORS.primary, border: `1px solid ${COLORS.primary}`, borderRadius: '8px' }}>
              <i className="bi bi-arrow-clockwise me-1"></i> Actualiser
            </button>
          </div>
          <div className="card-body pt-0 d-flex flex-wrap gap-2">
            {['tous', 'web', 'mobile'].map(key => {
              const meta = key === 'tous'
                ? { label: 'Tous', color: COLORS.primary, bg: COLORS.lightBg }
                : key === 'web'
                ? { label: 'Web', color: COLORS.secondary, bg: '#E3F2FD' }
                : { label: 'Mobile', color: '#6D28D9', bg: '#EDE7F6' };
              const count = key === 'tous' ? logs.length : (compteurs[key] || 0);
              return (
                <button
                  key={key}
                  className="btn btn-sm fw-semibold"
                  onClick={() => setFiltrePlateforme(key)}
                  style={{
                    backgroundColor: filtrePlateforme === key ? meta.color : meta.bg,
                    color: filtrePlateforme === key ? COLORS.white : meta.color,
                    border: `1px solid ${meta.color}`,
                    borderRadius: '999px',
                    padding: '6px 14px',
                  }}
                >
                  <i className={`bi ${key === 'mobile' ? 'bi-phone' : key === 'web' ? 'bi-display' : 'bi-collection'} me-1`}></i>
                  {meta.label} ({count})
                </button>
              );
            })}
          </div>
        </div>

        {loading ? (
          <div className="text-center py-5"><div className="spinner-border" style={{ color: COLORS.primary }}></div></div>
        ) : (
          <div className="table-responsive">
            <table className="table align-middle" style={{ backgroundColor: COLORS.white, borderRadius: '8px' }}>
              <thead>
                <tr style={{ backgroundColor: COLORS.lightBg }}>
                  <th>Date</th>
                  <th>Utilisateur</th>
                  {isAdmin && <th>Société</th>}
                  <th>Action</th>
                  <th>Description</th>
                  <th>Plateforme</th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 ? (
                  <tr><td colSpan={isAdmin ? 6 : 5} className="text-center text-muted py-4">Aucune entrée pour ce filtre.</td></tr>
                ) : (
                  logs.map((l) => (
                    <tr key={l.id_log}>
                      <td style={{ fontSize: '0.85rem' }}>{formatDate(l.created_at)}</td>
                      <td>
                        {l.nom_user ? (
                          <>
                            <div>{l.prenom_user} {l.nom_user}</div>
                            <small className="text-muted">{PROFILE_LABELS[l.profile_user] || '-'}</small>
                          </>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      {isAdmin && <td>{l.nom_societe || '-'}</td>}
                      <td><code style={{ fontSize: '0.8rem' }}>{l.action}</code></td>
                      <td style={{ fontSize: '0.9rem' }}>{l.description}</td>
                      <td>
                        <span
                          className="badge d-inline-flex align-items-center gap-1"
                          style={{
                            backgroundColor: l.plateforme === 'mobile' ? '#EDE7F6' : '#E3F2FD',
                            color: l.plateforme === 'mobile' ? '#6D28D9' : COLORS.secondary,
                            padding: '6px 10px', borderRadius: '999px', fontWeight: '600'
                          }}
                        >
                          <i className={`bi ${l.plateforme === 'mobile' ? 'bi-phone' : 'bi-display'}`}></i>
                          {l.plateforme === 'mobile' ? 'Mobile' : 'Web'}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </Layout>
  );
};

export default Journaux;
